<template>
  <USelectMenu
    v-model="currentValue"
    :items="items"
    :color="inputColor"
    label-key="title"
    by="idService"
    :placeholder="$t('components.service.elements.select.title')"
    :filter-fields="['title', 'information']"
    v-bind="$attrs"
  >
    <template #default="{ modelValue: selected }">
      <div v-if="selected" class="flex items-center">
        <o-avatar :items="[selected]" class="hidden md:flex pr-2 shrink-0" />
        <div>
          <p class="text-gray-800 line-clamp-1 dark:text-gray-100">
            {{ selected.title }}
          </p>
        </div>
      </div>
    </template>

    <template #item="{ item }">
      <div v-if="item" class="flex items-center">
        <o-avatar :items="[item]" class="hidden md:flex pr-2 shrink-0" />
        <div>
          <p class="text-sm text-gray-700 line-clamp-1 dark:text-gray-200">
            {{ item.title }}
          </p>
        </div>
      </div>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

import {
  OAvatar,
} from '@/components/elements'

interface Props {
  modelValue?: any
  type?: string | null
  addOnMode?: any[] | null
  allowInternet?: boolean | null
  fieldClass?: string
  status?: string | boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  type: null,
  addOnMode: null,
  allowInternet: null,
  fieldClass: 'text-sm',
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'change'): void
}>()

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
    emit('change')
  },
})

const services = computed(() => serviceStore.all)

const inputColor = computed(() => {
  if (props.status === false || props.status === 'error') return 'error' as const
  if (props.status === 'warning') return 'warning' as const
  if (props.status === true || props.status === 'success') return 'success' as const
  return 'neutral' as const
})

const items = computed(() => {
  const type = props.type
  const addOnMode = props.addOnMode

  const categories: Record<string, any> = {}

  for (const service of services.value) {
    if (type === 'group' && !service.isServiceCustomerTypeGroup) continue
    if (type === 'single' && !service.isServiceCustomerTypeSingle) continue
    if (addOnMode && !addOnMode.includes(service.addOnMode)) continue
    if (props.allowInternet === true && !service.allowInternet) continue

    const category = service.category
    const idServiceCategory = category?.idServiceCategory || 0

    if (!categories[idServiceCategory]) {
      categories[idServiceCategory] = {
        label: category ? category.name : '',
        order: category ? category.listOrder : 0,
        items: [],
      }
    }

    categories[idServiceCategory].items.push(service)
  }

  const sorted = Object.values(categories).sort((a: any, b: any) => a.order - b.order)

  return sorted.map((cat: any) => [
    { type: 'label' as const, label: cat.label },
    ...[...cat.items].sort((left: any, right: any) => {
      const leftOrder = Number.isFinite(Number(left.listOrder)) ? Number(left.listOrder) : Number.MAX_SAFE_INTEGER
      const rightOrder = Number.isFinite(Number(right.listOrder)) ? Number(right.listOrder) : Number.MAX_SAFE_INTEGER
      return leftOrder - rightOrder
    }),
  ])
})

onMounted(() => {
  serviceStore.sync()
  employeeStore.sync()
})
</script>
